// SPDX-License-Identifier: BUSL-1.1

/**
 * Usage dashboard page — per-org usage metrics (agent cost, session counts, time series).
 *
 * Shows summary cards (total cost, active time, sessions, success rate),
 * a time-series chart (7d / 30d / 90d / 365d), and a breakdown table by agent.
 *
 * Backend: GET /api/tenant/usage?period=<7d|30d|90d|365d>
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BarChart3, Clock, DollarSign, CheckCircle2, XCircle, Bot } from 'lucide-react';
import { TableSkeleton } from '@/components/skeletons';
import { DataTable } from '@/components/data-table';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { apiFetch } from '@/lib/api-client';
import type { UsageSummary, UsageTimeSeriesEntry, UsageBreakdownEntry } from '@/types/usage';
import { formatCost } from '@build-ceo/shared-constants';

interface UsageResponse {
  summary: UsageSummary;
  timeSeries: UsageTimeSeriesEntry[];
  breakdown: UsageBreakdownEntry[];
}

function StatCard({
  label,
  value,
  icon: Icon,
  sub,
}: {
  label: string;
  value: string;
  icon: typeof DollarSign;
  sub?: string;
}) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-neutral-800 bg-neutral-900/50 p-4">
      <div className="rounded-lg bg-neutral-800 p-2">
        <Icon className="size-5 text-neutral-400" />
      </div>
      <div>
        <p className="text-xs font-medium tracking-wider text-neutral-500 uppercase">{label}</p>
        <p className="mt-0.5 font-mono text-xl font-semibold">{value}</p>
        {sub && <p className="mt-0.5 text-xs text-neutral-500">{sub}</p>}
      </div>
    </div>
  );
}

export function UsageDashboardPage() {
  const [period, setPeriod] = useState('30d');

  const { data, isLoading, error } = useQuery<UsageResponse>({
    queryKey: ['tenant-usage', period],
    queryFn: () => apiFetch<UsageResponse>(`/tenant/usage?period=${period}`),
    staleTime: 5 * 60 * 1000, // 5 minutes — usage doesn't change second-by-second
    retry: 1,
  });

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <TableSkeleton cols={4} rows={4} />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="rounded-lg border border-red-900/30 bg-red-950/20 p-4 text-red-400">
        Failed to load usage data. Please try again.
      </div>
    );
  }

  const { summary, timeSeries, breakdown } = data;

  const successRate =
    summary.total_sessions > 0
      ? Math.round((summary.successful_sessions / summary.total_sessions) * 100)
      : 0;

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-mono text-2xl font-bold tracking-tight">Usage</h1>
          <p className="mt-1 text-sm text-neutral-500">
            Agent cost and activity for the selected organization
          </p>
        </div>
        <Tabs value={period} onValueChange={setPeriod}>
          <TabsList>
            <TabsTrigger value="7d">7d</TabsTrigger>
            <TabsTrigger value="30d">30d</TabsTrigger>
            <TabsTrigger value="90d">90d</TabsTrigger>
            <TabsTrigger value="365d">1y</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Total Cost"
          value={formatCost(summary.total_cost_usd)}
          icon={DollarSign}
          sub={`${summary.total_jobs} jobs`}
        />
        <StatCard
          label="Active Time"
          value={
            summary.total_active_time_ms > 0 ? formatDuration(summary.total_active_time_ms) : '—'
          }
          icon={Clock}
          sub={`${summary.total_jobs} jobs`}
        />
        <StatCard
          label="Sessions"
          value={summary.total_sessions.toLocaleString()}
          icon={BarChart3}
          sub={`${summary.active_agents} active agents`}
        />
        <StatCard
          label="Success Rate"
          value={`${successRate}%`}
          icon={successRate >= 90 ? CheckCircle2 : XCircle}
          sub={`${summary.successful_sessions} / ${summary.total_sessions} sessions`}
        />
      </div>

      {/* Time series chart */}
      {timeSeries.length > 0 ? (
        <div className="rounded-lg border border-neutral-800 bg-neutral-900/50 p-6">
          <h2 className="mb-4 font-mono text-sm font-semibold text-neutral-400">Cost Over Time</h2>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={timeSeries}>
              <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
              <XAxis
                dataKey="period"
                tick={{ fill: '#737373', fontSize: 11 }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                tick={{ fill: '#737373', fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v) => `$${v}`}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#171717',
                  border: '1px solid #262626',
                  borderRadius: '6px',
                  fontSize: '12px',
                  color: '#a3a3a3',
                }}
                labelStyle={{ color: '#d4d4d4' }}
                formatter={(value) => [`$${Number(value).toFixed(4)}`, 'Cost']}
              />
              <Legend wrapperStyle={{ fontSize: '12px', color: '#737373' }} />
              <Bar dataKey="cost" name="Cost ($)" fill="#10b981" radius={[4, 4, 0, 0]} />
              <Bar dataKey="sessions" name="Sessions" fill="#6366f1" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="rounded-lg border border-neutral-800 bg-neutral-900/50 p-6 text-center text-sm text-neutral-500">
          No usage data for this period yet.
        </div>
      )}

      {/* Breakdown table */}
      {breakdown.length > 0 ? (
        <div className="overflow-hidden rounded-lg border border-neutral-800 bg-neutral-900/50">
          <div className="border-b border-neutral-800 px-6 py-4">
            <h2 className="font-mono text-sm font-semibold text-neutral-400">Breakdown by Agent</h2>
          </div>
          <div className="overflow-x-auto">
            <DataTable
            columns={[
              {
                accessorKey: 'agent_name',
                header: 'Agent',
                cell: ({ row }) => (
                  <div className="flex items-center gap-2">
                    <Bot className="size-4 text-neutral-500" />
                    <span className="font-mono text-neutral-200">{row.original.agent_name}</span>
                  </div>
                ),
              },
              {
                accessorKey: 'job_count',
                header: 'Jobs',
                cell: ({ row }) => (
                  <span className="font-mono text-neutral-400">{row.original.job_count}</span>
                ),
              },
              {
                accessorKey: 'session_count',
                header: 'Sessions',
                cell: ({ row }) => (
                  <span className="font-mono text-neutral-400">{row.original.session_count}</span>
                ),
              },
              {
                accessorKey: 'total_active_time_ms',
                header: 'Active Time',
                cell: ({ row }) =>
                  row.original.total_active_time_ms > 0 ? (
                    <span className="font-mono text-neutral-400">{formatDuration(row.original.total_active_time_ms)}</span>
                  ) : (
                    '—'
                  ),
              },
              {
                accessorKey: 'total_cost_usd',
                header: 'Cost',
                cell: ({ row }) => (
                  <span className="text-right font-mono font-medium text-emerald-400">
                    {formatCost(row.original.total_cost_usd)}
                  </span>
                ),
              },
            ]}
            data={breakdown}
            emptyMessage="No usage data yet."
            pageSizeOptions={null}
          />
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-neutral-800 bg-neutral-900/50 p-6 text-center text-sm text-neutral-500">
          No agent breakdown available.
        </div>
      )}
    </div>
  );
}

/** Format milliseconds as human-readable duration (e.g., "2h 34m") */
function formatDuration(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ${hours % 24}h`;
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  if (minutes > 0) return `${minutes}m`;
  return `${seconds}s`;
}
